import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Star, X, Plus } from 'lucide-react';
import { useLiveTickers } from '../../lib/crypto/useBinanceStream';

const fmtUSD = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  if (v >= 1) return `$${v.toFixed(3)}`;
  return `$${v.toPrecision(4)}`;
};

/**
 * Watchlist — persistent per-user list of coins. Prices update every
 * ~1s via the shared Binance WebSocket stream, so a busy pair like
 * BTC/ETH ticks visibly the same way it does on TradingView.
 */
export default function Watchlist({ current, onPick, refreshTick }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/crypto/watchlist');
      setItems(data?.items || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, [refreshTick]);

  // Live tick stream — combined Binance + Coinbase.
  const symbols = useMemo(() => items.map((i) => i.symbol), [items]);
  const quotes = useLiveTickers(symbols);

  const sorted = useMemo(() => {
    const rows = items.map((i) => ({ ...i, q: quotes[i.symbol] }));
    rows.sort((a, b) => {
      if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
      return a.symbol.localeCompare(b.symbol);
    });
    return rows;
  }, [items, quotes]);

  const toggleFavorite = async (sym, cur) => {
    setBusy(true);
    try {
      const { data } = await api.post('/crypto/watchlist/favorite', { symbol: sym, favorite: !cur });
      setItems(data?.items || []);
    } finally { setBusy(false); }
  };

  const remove = async (sym) => {
    setBusy(true);
    try {
      const { data } = await api.post('/crypto/watchlist/toggle', { symbol: sym });
      setItems(data?.items || []);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="crypto-watchlist">
      <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys px-2 py-1 border-b border-[var(--border-default)]">
        // watchlist ({sorted.length})<span className="ml-2 text-[8px] text-buy">● LIVE</span>
      </div>
      {sorted.length === 0 && (
        <div className="p-3 text-[11px] text-[var(--txt-muted)] leading-relaxed">
          Empty. Search a coin and hit the <span className="text-buy">+</span> icon on the price card
          to add it to your watchlist. Favorites float to the top.
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {sorted.map((r) => {
          const q = r.q || {};
          const chg = q.change_pct;
          const active = current === r.symbol;
          return (
            <div
              key={r.symbol}
              data-testid={`crypto-watch-row-${r.symbol}`}
              onClick={() => onPick?.(r)}
              className={`grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-2 py-1.5 border-b border-[var(--border-default)] cursor-pointer group ${active ? 'bg-cyan-sys/10' : 'hover:bg-[var(--bg-panel-hover)]'}`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); toggleFavorite(r.symbol, r.favorite); }}
                disabled={busy}
                className={r.favorite ? 'text-hold' : 'text-[var(--txt-muted)] hover:text-hold'}
                aria-label="favorite"
              >
                <Star size={11} fill={r.favorite ? 'currentColor' : 'none'} />
              </button>
              <div className="min-w-0">
                <div className={`text-[11px] font-mono truncate ${active ? 'text-cyan-sys' : 'text-[var(--txt-primary)]'}`}>{r.base}</div>
                <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">{r.quote}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-mono text-[var(--txt-primary)] tabular-nums" data-testid={`crypto-watch-last-${r.symbol}`}>
                  {q.last != null ? fmtUSD(q.last) : '—'}
                </div>
                <div className={`text-[9px] font-mono tabular-nums ${chg == null ? 'text-[var(--txt-muted)]' : chg >= 0 ? 'text-buy' : 'text-sell'}`}>
                  {chg == null ? '—' : `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%`}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); remove(r.symbol); }}
                className="opacity-0 group-hover:opacity-100 text-[var(--txt-muted)] hover:text-sell transition-opacity"
                aria-label="remove"
                data-testid={`crypto-watch-remove-${r.symbol}`}
              >
                <X size={11} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
